/**
 * Figma design-system primitives — single source of truth.
 * Source: https://www.figma.com/design/5BmbmNbmKbPEtDEOp1OWpL/  node-id=359-5091
 *
 * Every hex value is taken verbatim from the Figma variable definitions.
 * Do NOT edit individual values here; update the Figma file first, then sync.
 */

import type { ColorScale, Primitives } from './types';

// ---------------------------------------------------------------------------
// Base (absolute, mode-independent)
// ---------------------------------------------------------------------------

export const base = {
    black: '#000000',
    white: '#ffffff',
} as const;

// ---------------------------------------------------------------------------
// Raw color scales  (Figma "Dark" mode column — canonical direction)
// ---------------------------------------------------------------------------

export const woodsmoke: ColorScale = {
    50: '#fcfcfc',
    100: '#f7f7f7',
    200: '#f5f5f5',
    300: '#e5e5e5',
    400: '#d4d4d4',
    500: '#a3a3a3',
    600: '#737373',
    700: '#525252',
    800: '#424242',
    900: '#292929',
    950: '#141414',
    1000: '#0f0f0f',
};

export const blueribbon: ColorScale = {
    50: '#f7faff',
    100: '#f1f8ff',
    200: '#d6ebff',
    300: '#b9ddff',
    400: '#8ac6ff',
    500: '#52a2ff',
    600: '#317efe',
    700: '#215eee',
    800: '#214cd3',
    900: '#1c3ca7',
    950: '#162d78',
    1000: '#0e2054',
};

export const thunderbird: ColorScale = {
    50: '#fffbf9',
    100: '#fef3f1',
    200: '#ffe4e0',
    300: '#ffcec5',
    400: '#fea194',
    500: '#fa6f5f',
    600: '#f24432',
    700: '#db2e1a',
    800: '#b52213',
    900: '#921f15',
    950: '#7a2517',
    1000: '#55160b',
};

export const shamrock: ColorScale = {
    50: '#f6fef9',
    100: '#effdf4',
    200: '#dafae7',
    300: '#a6f0c9',
    400: '#6ce2ac',
    500: '#36cf8f',
    600: '#00b471',
    700: '#00955b',
    800: '#00774c',
    900: '#005e3e',
    950: '#004e34',
    1000: '#013423',
};

export const sun: ColorScale = {
    50: '#fffcf2',
    100: '#fffbe9',
    200: '#fff4c4',
    300: '#ffe890',
    400: '#fed863',
    500: '#fdbc18',
    600: '#f1a000',
    700: '#d27a00',
    800: '#aa5401',
    900: '#8b430a',
    950: '#753610',
    1000: '#4e1f0a',
};

export const riptide: ColorScale = {
    50: '#f5fefc',
    100: '#effdf9',
    200: '#c7fcef',
    300: '#8ef8e0',
    400: '#43ecd0',
    500: '#00d6b7',
    600: '#00b99e',
    700: '#009585',
    800: '#00776a',
    900: '#005e57',
    950: '#034f49',
    1000: '#013834',
};

// ---------------------------------------------------------------------------
// Mirror utility — inverts a 12-step scale (50↔1000, 100↔950, …, 500↔600)
// This matches Figma's variable mode resolution (Dark ↔ Light).
// ---------------------------------------------------------------------------

function mirrorScale(scale: ColorScale): ColorScale {
    return {
        50: scale[1000],
        100: scale[950],
        200: scale[900],
        300: scale[800],
        400: scale[700],
        500: scale[600],
        600: scale[500],
        700: scale[400],
        800: scale[300],
        900: scale[200],
        950: scale[100],
        1000: scale[50],
    };
}

// ---------------------------------------------------------------------------
// Mode-resolved primitive sets
//
// Dark mode  = raw Figma values (50 = lightest, 1000 = darkest)
// Light mode = woodsmoke mirrored; brand families stay the same.
//
// After resolution, shade numbers have consistent SEMANTIC meaning:
//   50  = highest-contrast foreground  (white in dark, black in light)
//   500 = mid-tone
//   1000 = deepest background          (black in dark, white in light)
// ---------------------------------------------------------------------------

export const darkPrimitives: Primitives = {
    base,
    woodsmoke,
    blueribbon,
    thunderbird,
    shamrock,
    sun,
    riptide,
};

export const lightPrimitives: Primitives = {
    base,
    woodsmoke: mirrorScale(woodsmoke),
    blueribbon,
    thunderbird,
    shamrock,
    sun,
    riptide,
};

/** Raw primitives (same as darkPrimitives). Useful for direct shade access. */
export const primitives = darkPrimitives;
